import { LocationState } from 'history';
import { useInjection } from 'inversify-react';
import { useEffect } from 'react';
import { matchPath } from 'react-router';
import { usePrevious } from '../../../design/hooks/usePrevious';
import { UUIDRegex } from '../../helpers/string.helper';
import { ACDLError } from '../../models/error.model';
import { Auth } from '../../routes/config/Auth';
import { Home } from '../../routes/config/Home';
import { IMainApiService } from '../../services/api.service';
import { IBrowserService } from '../../services/browser.service';
import { ICryptoService } from '../../services/crypto.service';
import { IWorkspaceService } from '../workspaces/workspaces.service';
import { IConfirmEmailModel, ILoginModel, IRegisterModel, IResendEmailModel } from './auth.model';
import { IAuthStore } from './auth.store';

const apiUrl = '/Authorization/';

const IGNORED_ROUTE: string[] = [Auth.Register.Path, Auth.Login.Path, Auth.ForgotPassword.Path, Auth.ResetPassword.Path];

interface IReffererRoute {
  from: {
    pathname: string;
    search: string;
    hash: string;
  };
}

export interface IAuthService {
  authenticate(loginModel: ILoginModel): Promise<void | ACDLError>;
  register(registerModel: IRegisterModel): Promise<void | ACDLError>;
  adminRegister(registerModel: IRegisterModel): Promise<void | ACDLError>;
  confirmEmail(confirmEmailModel: IConfirmEmailModel): Promise<void | ACDLError>;
  confirmNewEmail(confirmEmailModel: IConfirmEmailModel): Promise<void | ACDLError>;
  resendConfirmationEmail(resendEmailModel: IResendEmailModel): Promise<void | ACDLError>;
  logout(): void;
  requestPasswordRequest(email: string): Promise<void | ACDLError>;
  resetPassword(token: string, newPassword: string): Promise<void | ACDLError>;
  setRouteReferrer(route: LocationState): void;
  getAndClearReferrer(): string;
  showVersionChangedMessage(changed?: boolean): void;
  setLockoutDate(lockoutDate: string | undefined): void;
}

export const useAuthService = (): IAuthService => {
  const data = useInjection(IAuthStore.$);
  const apiService = useInjection(IMainApiService.$);
  const browserService = useInjection(IBrowserService.$);
  const workspaceService = useInjection(IWorkspaceService.$);
  const cryptoService = useInjection(ICryptoService.$);

  const previousToken = usePrevious(data.token);

  useEffect(() => {
    if (previousToken && !data.token) {
      logout();
    }
  }, [data.token]);

  const setLockoutDate = (lockoutDate: string | undefined): void => {
    data.setLockoutDate(lockoutDate);
  };

  const showVersionChangedMessage = (changed?: boolean): void => {
    data.setVersionChanged(changed);
  };

  const authenticate = async (loginModel: ILoginModel): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}Authenticate`;
    const result = await apiService.postAsync<string>(url, loginModel);

    if (result instanceof ACDLError) {
      return result;
    }

    data.updateToken(result);
    data.setShowPlanModal(true);

    return undefined;
  };

  const logout = (): void => {
    browserService.clearCallBacks();
    data.clearToken();
    cryptoService.clearAll();
    window.location.replace('/');
  };

  const register = async (registerModel: IRegisterModel): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}Register`;
    const result = await apiService.postAsync<IRegisterModel>(url, registerModel);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const adminRegister = async (registerModel: IRegisterModel): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}AdminRegisterUser`;
    const result = await apiService.postAsync<IRegisterModel>(url, registerModel);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const confirmEmail = async (confirmEmailModel: IConfirmEmailModel): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}ConfirmEmail`;
    const result = await apiService.postAsync<string>(url, confirmEmailModel);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const confirmNewEmail = async (confirmEmailModel: IConfirmEmailModel): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}ChangeEmail`;
    const result = await apiService.postAsync<string>(url, confirmEmailModel);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const resendConfirmationEmail = async (resendEmailModel: IResendEmailModel): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}ResendConfirmationEmail`;
    const result = await apiService.postAsync<boolean>(url, resendEmailModel);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const requestPasswordRequest = async (email: string): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}RequestResetPassword`;
    const result = await apiService.postAsync<{ email: string }>(url, { email });

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const resetPassword = async (token: string, newPassword: string): Promise<ACDLError | undefined> => {
    const url = `${apiUrl}ResetPassword`;
    const result = await apiService.postAsync<{ token: string; newPassword: string }>(url, {
      token,
      newPassword,
    });

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  };

  const setRouteReferrer = (route: IReffererRoute): void => {
    if (route !== undefined && !IGNORED_ROUTE.includes(route.from.pathname)) {
      const match = matchPath<{ workspaceId: string }>(route.from.pathname, { path: Home.Path });
      if (match && UUIDRegex.test(match.params.workspaceId)) {
        data.setRouteReferrer(route.from.pathname);
        return;
      }
    }
    data.setRouteReferrer('');
  };

  const getAndClearReferrer = (): string => {
    const refferer = data.routeReferrer;
    data.setRouteReferrer('');
    if (refferer) {
      return refferer;
    }

    const preferredWorkspaceId = workspaceService.store.preferredWorkspaceId;
    if (preferredWorkspaceId) {
      return Home.Projects.List.All.withParams({ workspaceId: preferredWorkspaceId });
    }

    const firstAvailable = workspaceService.store.workspaces[0].id;
    return Home.Projects.List.All.withParams({ workspaceId: firstAvailable });
  };

  return {
    authenticate,
    register,
    confirmEmail,
    confirmNewEmail,
    resendConfirmationEmail,
    logout,
    requestPasswordRequest,
    resetPassword,
    setRouteReferrer,
    getAndClearReferrer,
    showVersionChangedMessage,
    setLockoutDate,
    adminRegister,
  };
};
